import { toUserAllowance } from '../../models/users/user-allowance.model';
import { PatientDetailsDto } from "../../dto/patients/patient-details.dto";
import { PatientDto } from "../../dto/patients/patient.dto";
import { PatientDetails } from "../../models/patients/patient-details.model";
import { Patient } from "../../models/patients/patient.model";
import { OnboardingStatus, parseGender, SubriseUser, SubriseUserDetails, UserRole } from "../../models/users/subrise-user.model";
import { StoreSubscriptionMapper } from "../store/store-subscription.mapper";
import { UserContentPreferencesMapper } from "../users/user-content-preferences.mapper";

export class PatientMapper {
  public static toModel(dto: PatientDto): Patient {
    return {
      id: dto.id,
      userId: dto.user_id,
      firebaseUserId: dto.firebase_user_id || '',
      userName: dto.user_name || '',
      email: dto.email || '',
      displayName: dto.display_name || '',
      firstName: dto.first_name || '',
      lastName: dto.last_name || '',
      gender: parseGender(dto.gender),
      localeName: dto.locale_name || '',
      languageId: dto.language_id,
      onboardingStatus: dto.onboarding_status || OnboardingStatus.NotStarted,
      isTest: dto.is_test || false,
      profilePictureUrl: dto.profile_picture_url || '',
      createdAt: dto.created_at ? new Date(dto.created_at) : null,
      sessionCount: dto.session_count || 0,
      consents: {
        moodReportsSince: dto.mood_reports_consent_at ? new Date(dto.mood_reports_consent_at) : null,
        sessionActivitySince: dto.session_activity_consent_at ? new Date(dto.session_activity_consent_at) : null,
        premiumAllowanceSince: dto.premium_allowance_consent_at ? new Date(dto.premium_allowance_consent_at) : null,
      },
      isPracticeOwner: dto.is_practice_owner ?? false,
    }
  }

  public static toDetailedModel(dto: PatientDetailsDto): PatientDetails {
    return {
      isDetailed: true,
      ...this.toModel(dto),
      ...UserContentPreferencesMapper.toModel(dto),
      birthday: dto.birthday ? new Date(dto.birthday) : null,
      firstNamePhoneticSpelling: dto.first_name_phonetic_spelling || '',
      occupation: dto.occupation || '',
      companyName: dto.company_name || '',
      favoritePlace: dto.favorite_place || '',
      anchoringAction: dto.anchoring_action || '',
      anchoringWord: dto.anchoring_word || '',
      selfHypnosisAction: dto.self_hypnosis_action || '',
      preferredVoiceId: dto.preferred_voice_id ?? undefined,
      smartUserProgramIds: dto.smart_user_program_ids || [],
      subscriptions: (dto.subscriptions || []).map((subscription) => StoreSubscriptionMapper.toModel(subscription)),
      allowance: toUserAllowance(dto.allowance),
    }
  }

  /**
   * A patient as the app's own user model, so the client screens can render one without
   * knowing it came from the practice endpoints. `id` is the string user id the rest of the
   * app keys a user by — the numeric patient id stays with the endpoints that ask for it.
   */
  public static toSubriseUser(patient: Patient): SubriseUser {
    return {
      id: patient.userId,
      firstName: patient.firstName,
      lastName: patient.lastName,
      userName: patient.userName,
      displayName: patient.displayName,
      email: patient.email,
      gender: patient.gender ?? '',
      isTest: patient.isTest,
      localeName: patient.localeName,
      languageId: patient.languageId,
      profilePictureUrl: patient.profilePictureUrl,
      sessionCount: patient.sessionCount,
      createdAt: patient.createdAt,
      // A patient is an end user of a practice, which is what keeps them from being deleted here.
      roles: [UserRole.EndUser],
      isLinkedToPartner: true,
      isPracticeOwner: patient.isPracticeOwner
    }
  }

  public static toSubriseUserDetails(patient: PatientDetails): SubriseUserDetails {
    return {
      ...this.toSubriseUser(patient),
      isDetailed: true,
      preferredAddressingStyle: patient.preferredAddressingStyle,
      preferredCommunicationStyle: patient.preferredCommunicationStyle,
      preferredSubconsciousContent: patient.preferredSubconsciousContent,
      preferredContentPersonalizationLevel: patient.preferredContentPersonalizationLevel,
      preferredSessionDuration: patient.preferredSessionDuration,
      occupation: patient.occupation,
      birthday: patient.birthday,
      companyName: patient.companyName,
      favoritePlace: patient.favoritePlace,
      anchoringAction: patient.anchoringAction,
      anchoringWord: patient.anchoringWord,
      selfHypnosisAction: patient.selfHypnosisAction,
      onboardingStatus: patient.onboardingStatus,
      preferredVoiceId: patient.preferredVoiceId,
      firstNamePhoneticSpelling: patient.firstNamePhoneticSpelling,
      smartUserProgramIds: patient.smartUserProgramIds,
      subscriptions: patient.subscriptions,
      patientConsents: patient.consents,
      allowance: patient.allowance,
    }
  }
}
